import { Component, CSSProperties } from 'react';
import _ from 'lodash';
import { escapeStringRegExp } from '@Utils/Utils';
import { Localization } from './Localization/Localization';
import { isDefined, IsNullOrEmpty, IsNullOrWhiteSpace } from '@Utils/Micro';
import { DebugEx } from '@Utils/DebugEx';
import Toggle from '@Primitive/Toggle';
import ListItem from '@Primitive/ListItem';
import Popover from '@Primitive/Popover';
import TextBox from '@Primitive/TextBox';
import { SearchBarGetLocalizationDictionary, SearchBarLocalizationTextMapKey, SearchBarUILabelKey } from './SearchBarLocalization';
// ------------------------------------------------------------------------------------------------
export interface SearchBarProps {
    style: CSSProperties;
    source: { key: string, value: string }[];
    onSearch: (keys: string[], emptyValue: boolean, searchValue: string) => void;
    simple?: boolean;
    iconColor?: string;
    hideSearchIcon?: boolean;
    localizationKey?: string;
    autoFocus?: boolean;
    defaultSearchValue?: string;
    iconButtonStyle?: CSSProperties;
}
export interface SearchBarState {
    searchValue: string;
    isRegExpValid: boolean;
    useRegExp: boolean;
    matchCase: boolean;
    matchWholeWord: boolean;
}
// ------------------------------------------------------------------------------------------------
export class SearchBar extends Component<SearchBarProps, SearchBarState> {
    private static _GetLocalizedLabels(localizationKey: string): any {

        let defaultValues = SearchBarGetLocalizationDictionary();
        if (!isDefined(localizationKey))
            return defaultValues;

        let localization = Localization.GetInstance(localizationKey);
        if (!isDefined(localization))
            return defaultValues;

        let map = localization.GetTextMap(SearchBarLocalizationTextMapKey);
        if (!isDefined(map))
            return defaultValues;

        let res = defaultValues;
        _.forEach(map, (value, key) => {
            if (!IsNullOrEmpty(value))
                res[key] = value;
        });
        return res;
    }
    // ------------------------------------------------------------------------------------------------
    constructor(props: SearchBarProps) {
        super(props);
        this.state = {
            searchValue: props.defaultSearchValue ?? '',
            matchCase: false,
            matchWholeWord: false,
            useRegExp: false,
            isRegExpValid: true
        };
    }
    // ------------------------------------------------------------------------------------------------
    private getFilteredSource(searchValue: string) {
        let filteredKeys: string[] = [];
        try {
            if (!IsNullOrWhiteSpace(searchValue)) {
                // If user regular expression is set escape special characters.
                if (!this.state.useRegExp)
                    searchValue = escapeStringRegExp(searchValue);

                // Create regular expression.
                let regExp: RegExp = new RegExp(
                    (this.state.matchWholeWord ? '\\b' : '') + searchValue + (this.state.matchWholeWord ? '\\b' : ''),
                    this.state.matchCase ? '' : 'i'
                );

                // Filter dashboards by created regular expession.
                _.forEach(this.props.source, (kvpair, index) => {
                    if (kvpair.value.search(regExp) > -1)
                        filteredKeys.push(kvpair.key);
                });
            }
        }
        catch (err) {
            console.log(err.message);
            console.log(err.stack);
        }
        return filteredKeys;
    }
    // ------------------------------------------------------------------------------------------------
    handleChange = value => {
        let searchValue: string = value;
        let isRegExpValid = true;
        if (this.state.useRegExp) {
            try {
                new RegExp(searchValue);
                isRegExpValid = true;
            }
            catch (err) {
                isRegExpValid = false;
            }
        }
        this.setState({ searchValue: searchValue, isRegExpValid: isRegExpValid }, () => {
            try {
                if (isDefined(this.props.onSearch)) {
                    this.props.onSearch(
                        this.getFilteredSource(searchValue),
                        IsNullOrEmpty(searchValue), searchValue);
                }
            } catch (ex) {
                console.log(ex.stack);
                DebugEx.TraceError(ex);
            }
        });
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        const localizedLabels = SearchBar._GetLocalizedLabels(this.props.localizationKey);
        return <div className='primitive-searchbar' style={{
            height: 48,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            lineHeight: '48px',
            color: this.props.iconColor,
            ...this.props.style
        }}>
            <TextBox
                fullWidth
                type='text'
                autoFocus={this.props.autoFocus}
                value={this.state.searchValue}
                style={{
                    ...this.props.style,
                    color: this.props.iconColor
                }}
                onChangeString={this.handleChange}
                onEnterPress={() => this.handleChange(this.state.searchValue)}
                placeholder={localizedLabels[SearchBarUILabelKey.HintText]} />

            {this.props.hideSearchIcon ? null : IsNullOrWhiteSpace(this.state.searchValue) ? null :
                <i className='fas fa-times' style={{ cursor: 'pointer' }} onClick={() => this.handleChange('')} />}

            {this.props.hideSearchIcon ? null : !IsNullOrWhiteSpace(this.state.searchValue) ? null :
                <i className='fas fa-search' style={{ cursor: 'pointer' }} />}

            {this.props.simple ? null : <Popover
                outsideNode={<i
                    key='search-options-button'
                    className='fas fa-ellipsis-v center'
                    title={localizedLabels[SearchBarUILabelKey.Options]}
                    style={{
                        color: this.props.iconColor,
                        height: '100%',
                        minWidth: 35,
                        cursor: 'pointer'
                    }} />}
                boxNode={<>
                    <ListItem
                        key='use-regex-option'
                        primaryText={localizedLabels[SearchBarUILabelKey.UseRegEx]}
                        rightIcon={<Toggle
                            toggled={this.state.useRegExp}
                            onToggle={() => this.setState({ useRegExp: !this.state.useRegExp })}
                        />} />
                    <ListItem
                        key='match-case-option'
                        primaryText={localizedLabels[SearchBarUILabelKey.MatchCase]}
                        rightIcon={<Toggle
                            toggled={this.state.matchCase}
                            onToggle={() => this.setState({ matchCase: !this.state.matchCase })}
                        />} />
                    <ListItem
                        key='match-whole-word-option'
                        primaryText={localizedLabels[SearchBarUILabelKey.MatchWholeWord]}
                        rightIcon={<Toggle
                            toggled={this.state.matchWholeWord}
                            onToggle={() => this.setState({ matchWholeWord: !this.state.matchWholeWord })}
                        />} />
                </>} />
            }
        </div>;
    }
}
// ------------------------------------------------------------------------------------------------